import React, { useEffect, useState } from "react";
import "../App.css";
import NavBar from "./NavBar";
import { useJwt } from "react-jwt";
import axios from "axios";
import bg3 from "../img/bg3.jpg";
import heroBg from "../img/bg1.jpg";
import bgEvent from "../img/bg.jpg";
import testimonialsBg from "../img/testimonials_bg.jpg";
import ssa_image from "../img/maryam.jpg";
import he_kastina from "../img/HE_kastina.jpg";
import Footer from "./Footer";
import EventBlog from "./EventBlog";
import { Link, useHistory } from "react-router-dom";
import { BasedURL } from "../util/constants";

// Default images for leadership cards by order
const LEADERSHIP_IMGS = [he_kastina, bgEvent, ssa_image];

/**
 * Photo card with an offset decorative green border.
 * borderCorner: 'top-left' → border anchored top-left, photo offset right+down
 *               'bottom-right' → border anchored bottom-right, photo offset left+up
 */
function LeaderPhoto({ img, alt, borderCorner = "top-left" }) {
  const OFFSET = 48; // px the photo card is shifted away from the border corner
  const HEIGHT = 380;
  const containerH = HEIGHT + OFFSET;

  const isTopLeft = borderCorner === "top-left";

  // Green border covers ~60% width and ~85% height of container, anchored to its corner
  const borderStyle = isTopLeft
    ? { top: 0, left: 0, right: "30%", bottom: "15%" }
    : { bottom: 0, right: 0, left: "30%", top: "15%" };

  // White card fills the rest, offset from the border corner
  const cardStyle = isTopLeft
    ? { top: OFFSET, left: OFFSET, right: 0, bottom: 0 }
    : { top: 0, left: 0, right: OFFSET, bottom: OFFSET };

  return (
    <div style={{ position: "relative", height: containerH }}>
      {/* Decorative green border */}
      <div
        style={{
          position: "absolute",
          ...borderStyle,
          border: "6px solid #4ade80",
          borderRadius: 24,
        }}
      />
      {/* White photo card */}
      <div
        style={{
          position: "absolute",
          ...cardStyle,
          background: "white",
          borderRadius: 24,
          boxShadow: "0 8px 32px rgba(0,0,0,0.10)",
          overflow: "hidden",
        }}
      >
        <img
          src={img}
          alt={alt}
          style={{ width: "100%", height: "100%", objectFit: "cover", objectPosition: "top" }}
        />
      </div>
    </div>
  );
}

const HERO_DEFAULTS = {
  title: "Katsina State\nSustainable\nDevelopment Goals",
  buttonText: "View more",
  buttonLink: "/login",
  imageUrl: null,
};

export default function LandingPage() {
  const token = localStorage.getItem("access_token");
  const { decodedToken } = useJwt(token);
  const history = useHistory();

  const [hero, setHero] = useState(HERO_DEFAULTS);
  const [leadershipCards, setLeadershipCards] = useState([]);
  const [hoveredCard, setHoveredCard] = useState(null);
  const [leaderProfiles, setLeaderProfiles] = useState([]);
  const [stats, setStats] = useState([
    { value: "16", label: "Intervention" },
    { value: "400", label: "LGAs Covered" },
    { value: "40,000+", label: "Beneficiaries" },
    { value: "50+", label: "Partners" },
  ]);

  useEffect(() => {
    window.scrollTo(0, 0);
    axios.get(`${BasedURL}/hero-config`).then(r => setHero(r.data)).catch(() => {});
    axios.get(`${BasedURL}/leadership`).then(r => setLeadershipCards(r.data)).catch(() => {});
    axios.get(`${BasedURL}/leader-profiles`).then(r => setLeaderProfiles(r.data)).catch(() => {});
    axios.get(`${BasedURL}/stats-config`).then(r => {
      const d = r.data;
      setStats([
        { value: d.stat1Value, label: d.stat1Label },
        { value: d.stat2Value, label: d.stat2Label },
        { value: d.stat3Value, label: d.stat3Label },
        { value: d.stat4Value, label: d.stat4Label },
      ]);
    }).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen w-full bg-white">
      <NavBar username={decodedToken?.username} />

      {/* ── Hero ── */}
      <section className="relative w-full overflow-hidden" style={{ height: 420 }}>
        <img
          src={hero.imageUrl || heroBg}
          alt="hero"
          className="absolute inset-0 w-full h-full object-cover object-top"
        />
        <div
          className="absolute inset-0"
          style={{ background: "linear-gradient(to right, rgba(22,101,52,0.88) 0%, rgba(22,101,52,0.60) 40%, rgba(22,101,52,0.15) 70%, transparent 100%)" }}
        />
        <div className="relative z-10 h-full flex flex-col justify-center px-12 md:px-20 max-w-2xl">
          <h1 className="text-white font-extrabold leading-tight mb-8" style={{ fontSize: "clamp(2rem, 4vw, 3rem)" }}>
            {hero.title.split("\n").map((line, i) => <span key={i}>{line}<br /></span>)}
          </h1>
          <button
            onClick={() => history.push(hero.buttonLink || "/login")}
            className="text-white font-semibold text-sm px-7 py-2.5 rounded-md transition-all w-fit"
            style={{ border: "2px solid white", background: "transparent" }}
            onMouseEnter={e => { e.currentTarget.style.background = "rgba(255,255,255,0.15)"; }}
            onMouseLeave={e => { e.currentTarget.style.background = "transparent"; }}
          >
            {hero.buttonText || "View more"}
          </button>
        </div>
      </section>

      {/* ── Introduction ── */}
      <section className="py-12 bg-white">
        <div className="max-w-5xl mx-auto px-4 flex flex-col md:flex-row items-start gap-8">
          <div className="w-full md:w-[48%] flex-shrink-0">
            <img
              src={bg3}
              alt="Katsina"
              className="w-full rounded-xl object-cover"
              style={{ height: 290 }}
            />
          </div>
          <div className="w-full md:w-[52%] flex flex-col justify-center">
            <p className="text-gray-700 text-sm leading-relaxed text-justify mb-6">
              Welcome to the Katsina State Project Support Unit,
              Substainable Development Goals (SDG) Tracking Dashboard — your comprehensive portal
              for monitoring and evaluating the progress of our initiatives towards achieving the United Nations
              SDGs. This innovative dashboard serves as a transparent and dynamic platform, designed to empower
              citizens, policymakers, and stakeholders with real-time insights into the implementation and impact
              of our developmental programs.
            </p>
            <div>
              <Link to="/login" className="no-underline">
                <button
                  className="text-green-700 text-sm font-medium px-5 py-2 rounded-md transition-all"
                  style={{ border: "1.5px solid #15803d", background: "transparent" }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = "#f0fdf4"; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
                >
                  Read more
                </button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ── Values / Vision / Mission ── */}
      {leadershipCards.length > 0 && (
        <section className="py-14 bg-white">
          <div className="max-w-5xl mx-auto px-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {leadershipCards.map((card, i) => {
                const hovered = hoveredCard === card.id;
                return (
                  <div
                    key={card.id}
                    className="relative rounded-2xl overflow-hidden cursor-pointer"
                    style={{ height: 320 }}
                    onClick={() => history.push(`/leadership/${card.id}`)}
                    onMouseEnter={() => setHoveredCard(card.id)}
                    onMouseLeave={() => setHoveredCard(null)}
                  >
                    {/* Background image */}
                    <img
                      src={card.imageUrl || LEADERSHIP_IMGS[i % LEADERSHIP_IMGS.length]}
                      alt={card.title}
                      className="absolute inset-0 w-full h-full object-cover"
                    />

                    {/* Permanent bottom gradient so title is always readable */}
                    <div
                      className="absolute inset-0"
                      style={{ background: "linear-gradient(to top, rgba(22,101,52,0.80) 0%, rgba(22,101,52,0.20) 45%, transparent 70%)" }}
                    />

                    {/* Slide-up overlay */}
                    <div
                      className="absolute inset-0 flex flex-col items-center justify-center px-5 text-center"
                      style={{
                        background: "rgba(22,101,52,0.88)",
                        transform: hovered ? "translateY(0)" : "translateY(100%)",
                        transition: "transform 0.4s cubic-bezier(0.4,0,0.2,1)",
                      }}
                    >
                      <span className="text-white font-bold text-xl mb-3">{card.title}</span>
                      {(card.content || card.subtitle) && (
                        <span className="text-green-100 text-sm leading-relaxed line-clamp-6">
                          {card.content || card.subtitle}
                        </span>
                      )}
                    </div>

                    {/* Default title at bottom — hidden once overlay appears */}
                    <div
                      className="absolute bottom-0 left-0 right-0 flex items-center justify-center pb-6"
                      style={{
                        opacity: hovered ? 0 : 1,
                        transition: "opacity 0.2s ease",
                      }}
                    >
                      <span className="text-white font-bold text-xl drop-shadow">{card.title}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* ── Stats Banner ── */}
      <section className="relative w-full overflow-hidden">
        <img
          src={testimonialsBg}
          alt="stats background"
          className="absolute inset-0 w-full h-full object-cover object-center"
        />
        <div
          className="absolute inset-0"
          style={{ background: "rgba(0,80,80,0.72)" }}
        />
        <div className="relative z-10 py-12 px-6 max-w-5xl mx-auto w-full grid grid-cols-2 md:grid-cols-4 gap-8">
          {stats.map(({ value, label }) => (
            <div key={label} className="flex flex-col items-center text-white text-center">
              <span className="font-extrabold leading-none" style={{ fontSize: "clamp(1.8rem, 4vw, 3rem)", whiteSpace: "nowrap" }}>
                {value}
              </span>
              <span className="mt-2 text-sm font-normal tracking-wide opacity-90">{label}</span>
            </div>
          ))}
        </div>
      </section>

      {/* ── Leadership ── */}
      {leaderProfiles.length > 0 && (
        <section id="about" className="py-16" style={{ background: "#f7f9f7" }}>
          <div className="max-w-4xl mx-auto px-4">

            <div className="flex justify-center mb-16">
              <span className="bg-green-600 text-white text-sm font-semibold px-8 py-2 rounded-lg tracking-wide">
                Leadership
              </span>
            </div>

            {leaderProfiles.map((leader, i) => {
              const isLeft = leader.photoSide !== "right";
              const photoEl = (
                <div className="flex-shrink-0 w-full md:w-[46%]">
                  <LeaderPhoto
                    img={leader.imageUrl || (i % 2 === 0 ? he_kastina : ssa_image)}
                    alt={leader.name}
                    borderCorner={isLeft ? "top-left" : "bottom-right"}
                  />
                </div>
              );
              const textEl = (
                <div className="flex-1 text-center md:text-left">
                  <h3 className="font-bold text-xl text-gray-800 mb-1">{leader.name}</h3>
                  {leader.role && <p className="text-gray-500 text-sm mb-4">{leader.role}</p>}
                  {leader.bio && (
                    <p className="text-sm text-gray-600 leading-relaxed text-justify">{leader.bio}</p>
                  )}
                </div>
              );

              return (
                <div
                  key={leader.id}
                  className={`flex items-center gap-12 ${i < leaderProfiles.length - 1 ? "mb-24" : ""} ${isLeft ? "flex-col md:flex-row" : "flex-col-reverse md:flex-row"}`}
                >
                  {isLeft ? <>{photoEl}{textEl}</> : <>{textEl}{photoEl}</>}
                </div>
              );
            })}

          </div>
        </section>
      )}

      {/* ── Event & Blog ── */}
      <EventBlog />

      <Footer />
    </div>
  );
}
